import { useEffect, useState, useCallback, Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { usePMAuth } from '@/contexts/PMAuthContext';
import { Loader2, CreditCard, LogOut, RefreshCw, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PMSidebar } from '@/components/pm/PMSidebar';
import { PMHeader } from '@/components/pm/PMHeader';
import { usePMSessionTimeout } from '@/hooks/usePMSessionTimeout';
import SessionTimeoutCountdown from '@/components/SessionTimeoutCountdown';
import { Skeleton } from '@/components/ui/skeleton';

function PageLoadingFallback() {
  return (
    <div className="p-6 space-y-4 flex-1">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-4 w-96 opacity-60" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl mt-4" />
    </div>
  );
}

export default function PMLayout() {
  const { user, pmUser, loading, pmUserLoaded, signOut, activeProgramManagerId } = usePMAuth();
  const { showCountdown, countdownSeconds, resetTimer } = usePMSessionTimeout();
  const navigate = useNavigate();
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/pm/login', { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!loading && pmUserLoaded && user && !pmUser) {
      const timer = setTimeout(() => setWaitedTooLong(true), 8000);
      return () => clearTimeout(timer);
    }
    setWaitedTooLong(false);
  }, [loading, pmUserLoaded, user, pmUser]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/pm/login', { replace: true });
  }, [signOut, navigate]);

  // FIX 2: Only show full-page spinner on first load (no session yet).
  if ((loading || !pmUserLoaded) && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <CreditCard className="w-10 h-10 text-accent opacity-60" />
          <Loader2 className="w-7 h-7 text-accent animate-spin" />
          <p className="text-muted-foreground text-sm">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (pmUserLoaded && (!pmUser || !activeProgramManagerId)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 text-center max-w-md">
          <div className="rounded-full bg-destructive/10 p-6">
            <Shield className="w-12 h-12 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Access Denied</h2>
          <p className="text-muted-foreground">
            {waitedTooLong
              ? 'Unable to load your program manager profile. Please try again or contact your administrator.'
              : 'Your account does not have Program Manager access. Contact your administrator for access.'}
          </p>
          <div className="flex gap-3">
            {waitedTooLong && (
              <Button variant="outline" onClick={() => window.location.reload()}>
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            )}
            <Button variant="outline" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const contentReady = !!pmUser && !!activeProgramManagerId;

  return (
    <>
      {showCountdown && (
        <SessionTimeoutCountdown
          secondsLeft={countdownSeconds}
          onStayLoggedIn={resetTimer}
          portalName="PM Portal"
        />
      )}
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header always rendered once session exists */}
        <PMHeader />
        <div className="flex flex-1 overflow-hidden">
          <PMSidebar />
          {/* FIX 3: Nested Suspense — only content area suspends, sidebar/header stay visible */}
          <main className="flex-1 overflow-auto">
            {contentReady ? (
              <Suspense fallback={<PageLoadingFallback />}>
                <div className="p-6">
                  <Outlet />
                </div>
              </Suspense>
            ) : (
              <PageLoadingFallback />
            )}
          </main>
        </div>
      </div>
    </>
  );
}
